/* ═════════════════════════ Imperial Ledger ═════════════════════════════════
   A parchment-bound book of the user's "life characters" — the people who
   have shaped their journey. Reuses the SovereignsPath paper-page aesthetic
   so the two books feel like siblings on the same shelf.

   Pages:
     0 — Oxblood leather cover ("THE LEDGER OF SOULS")
     1 — Index / roster of all characters (tap a name to jump to its page)
     2..N — One character per spread:
         • Photograph (optional — falls back to a parchment silhouette)
         • Centered name in gold Cinzel
         • Hand-written-style description (italic Cormorant)
         • Edit pencil → inline edit name + description
         • Delete option
     N+1 — "Add a new character" page with file picker + name + description

   Storage: backend /api/ledger (zero-cost — reuses existing free Firebase
   pipeline for images, falls back to local disk). Images are optional.
   ─────────────────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BackPill } from './BackPill';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Pencil, Trash2, Image as ImageIcon,
  Check,
} from 'lucide-react';
import { BACKEND_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const GOLD       = '#D4AF37';
const GOLD_HI    = '#F5D97A';
const INK        = '#1a1208';

/* ─────────────────── Reusable parchment page chrome ─────────────────── */

const PaperPage = ({ children, side = 'right' }) => (
  <div
    className="absolute inset-0 flex flex-col"
    style={{
      background: 'linear-gradient(180deg, #f8ebca 0%, #f0dfa8 50%, #d8c084 100%)',
      borderRadius: side === 'right' ? '4px 14px 14px 4px' : '14px 4px 4px 14px',
      boxShadow:
        'inset 0 0 70px rgba(168,107,31,0.18), inset 0 0 12px rgba(82,46,8,0.5), 0 8px 28px rgba(0,0,0,0.6)',
      overflow: 'hidden',
    }}
  >
    {/* Decorative gold inner borders */}
    <div className="absolute inset-2 pointer-events-none" style={{ border: `2px solid ${GOLD}`, borderRadius: 6 }} />
    <div className="absolute inset-3.5 pointer-events-none" style={{ border: '1px solid rgba(168,107,31,0.5)', borderRadius: 4 }} />
    {['1.5rem', 'calc(100% - 1.5rem)'].map((y) =>
      ['1.5rem', 'calc(100% - 1.5rem)'].map((x) => (
        <span
          key={`${x}-${y}`}
          aria-hidden
          style={{
            position: 'absolute', left: x, top: y,
            width: 14, height: 14,
            transform: 'translate(-50%, -50%) rotate(45deg)',
            border: `1px solid ${GOLD}`,
            background: 'rgba(212,175,55,0.18)',
            pointerEvents: 'none',
          }}
        />
      ))
    )}
    {/* Spine-side shadow */}
    <div
      aria-hidden
      style={{
        position: 'absolute', top: 0, bottom: 0,
        [side === 'right' ? 'left' : 'right']: 0,
        width: 16,
        background: side === 'right'
          ? 'linear-gradient(90deg, rgba(0,0,0,0.45), transparent)'
          : 'linear-gradient(270deg, rgba(0,0,0,0.45), transparent)',
        pointerEvents: 'none',
      }}
    />
    {children}
  </div>
);

/* ─── Oxblood leather cover (page 0) — mirrors Royal Quote Book cover ─── */

const CoverPage = ({ profilePicture, nickname }) => {
  const initial = (nickname || 'A')[0].toUpperCase();
  return (
  <div
    className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 overflow-hidden"
    style={{
      // Deep oxblood leather (the OUTSIDE of the book, not parchment).
      background:
        'radial-gradient(ellipse at 50% 38%, #6e1620 0%, #4a0a14 45%, #2a040a 100%)',
      borderRadius: '4px 14px 14px 4px',
      boxShadow:
        'inset 0 0 90px rgba(0,0,0,0.55), inset 0 0 18px rgba(0,0,0,0.7), 0 12px 32px rgba(0,0,0,0.7)',
    }}
  >
    {/* Layer 1 — leather grain noise */}
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          'radial-gradient(circle at 18% 22%, rgba(255,255,255,0.04) 0%, transparent 30%),' +
          'radial-gradient(circle at 78% 28%, rgba(0,0,0,0.18) 0%, transparent 28%),' +
          'radial-gradient(circle at 30% 78%, rgba(0,0,0,0.22) 0%, transparent 30%),' +
          'radial-gradient(circle at 82% 76%, rgba(255,225,180,0.05) 0%, transparent 32%)',
        mixBlendMode: 'overlay',
        opacity: 0.85,
      }}
    />
    {/* Layer 2 — vignette */}
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.5) 100%)',
      }}
    />

    {/* Embossed gold frame stack */}
    <div
      aria-hidden
      className="absolute inset-3 pointer-events-none"
      style={{
        border: `2.5px solid ${GOLD}`,
        borderRadius: 8,
        boxShadow:
          'inset 0 0 0 1px rgba(255,241,184,0.4), 0 0 14px rgba(212,175,55,0.35)',
      }}
    />
    <div
      aria-hidden
      className="absolute inset-5 pointer-events-none"
      style={{ border: '1px solid rgba(212,175,55,0.55)', borderRadius: 5 }}
    />

    {/* Corner medallions — gold rosettes */}
    {[
      { top: 14, left: 14 },
      { top: 14, right: 14 },
      { bottom: 14, left: 14 },
      { bottom: 14, right: 14 },
    ].map((pos, i) => (
      <div
        key={i}
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          ...pos,
          width: 22, height: 22,
          transform: 'rotate(45deg)',
          background:
            'linear-gradient(135deg, #F5E6A0 0%, #D4AF37 45%, #8B6B1F 100%)',
          border: '1px solid rgba(60,40,8,0.6)',
          boxShadow: '0 0 8px rgba(212,175,55,0.55), inset 0 0 4px rgba(255,255,255,0.35)',
        }}
      />
    ))}

    {/* Dragon-seal watermark behind everything */}
    <img
      src="/icons/empire_seal.png"
      alt=""
      aria-hidden
      draggable={false}
      className="absolute pointer-events-none select-none"
      style={{
        width: '70%', height: 'auto',
        left: '50%', top: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: 0.07,
        filter: 'sepia(1) hue-rotate(-15deg) saturate(2.5) brightness(1.4)',
        mixBlendMode: 'screen',
      }}
    />

    {/* Spine shadow on bound (left) edge */}
    <div
      aria-hidden
      style={{
        position: 'absolute', top: 0, bottom: 0, right: 0,
        width: 18,
        background: 'linear-gradient(270deg, rgba(0,0,0,0.6), transparent)',
        pointerEvents: 'none',
      }}
    />

    {/* ── Cover content ───────────────────────────── */}
    <p
      className="font-['Cormorant_Garamond'] uppercase tracking-[0.42em] text-[11px] mb-6 relative z-10"
      style={{ color: '#F5E6A0', textShadow: '0 1px 0 rgba(0,0,0,0.45)' }}
    >
      ~ A Volume of Souls ~
    </p>

    {/* Profile medallion — user's own photo (mirrors Sovereign's Path cover) */}
    <div className="relative mb-6 z-10" style={{ width: 120, height: 120 }}>
      <div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(circle, rgba(245,217,122,0.55) 0%, rgba(212,175,55,0) 65%)',
          filter: 'blur(10px)',
          transform: 'scale(1.4)',
        }}
      />
      <div
        className="absolute inset-0 rounded-full overflow-hidden"
        style={{
          border: '3px solid #D4AF37',
          boxShadow:
            'inset 0 0 0 2px rgba(255,241,184,0.7),' +
            '0 0 20px rgba(212,175,55,0.55),' +
            '0 6px 18px rgba(0,0,0,0.55)',
          background: profilePicture
            ? '#2a040a'
            : 'radial-gradient(circle at 50% 32%, #f6e6b6, #c69b3f 70%, #6b4612 100%)',
        }}
      >
        {profilePicture ? (
          <img
            src={profilePicture}
            alt={nickname || 'Soul'}
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontWeight: 700,
              fontSize: 54,
              fontStyle: 'italic',
              color: '#4a0a14',
              textShadow: '0 1px 0 rgba(255,241,184,0.6)',
            }}
          >
            {initial}
          </div>
        )}
      </div>
    </div>

    {/* Title — embossed gold foil */}
    <h1
      className="font-['Cormorant_Garamond'] font-bold leading-none relative z-10"
      style={{
        fontSize: 'clamp(40px, 12vw, 64px)',
        letterSpacing: '0.06em',
        background:
          'linear-gradient(180deg, #FFF1B8 0%, #F5D97A 35%, #C69B3F 75%, #7A5418 100%)',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
        textShadow: '0 2px 0 rgba(0,0,0,0.4), 0 0 22px rgba(212,175,55,0.35)',
        filter: 'drop-shadow(0 1px 0 rgba(60,30,8,0.7))',
      }}
    >
      The Ledger
    </h1>

    <div
      className="my-5 w-32 h-[1.5px] relative z-10"
      style={{
        background: `linear-gradient(90deg, transparent, ${GOLD_HI}, transparent)`,
        boxShadow: '0 0 8px rgba(245,217,122,0.5)',
      }}
    />

    {/* Subtitle — italic gold */}
    <h2
      className="font-['Cormorant_Garamond'] italic font-light relative z-10"
      style={{
        color: '#F5E6A0',
        fontSize: 'clamp(20px, 5.6vw, 28px)',
        letterSpacing: '0.08em',
        textShadow: '0 1px 0 rgba(0,0,0,0.5)',
      }}
    >
      of Souls
    </h2>

    {/* Footer hint */}
    <p
      className="absolute bottom-7 left-0 right-0 text-center font-['Cormorant_Garamond'] italic text-[11px] z-10"
      style={{ color: 'rgba(245,230,160,0.65)' }}
    >
      — turn the page to inscribe a soul —
    </p>
  </div>
  );
};

/* ──── Header glyph — refined closed-book SVG matching the cover aesthetic ──── */

const LedgerHeaderGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <defs>
      <linearGradient id="ledgerHdrG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#F5D97A"/>
        <stop offset="55%" stopColor="#D4AF37"/>
        <stop offset="100%" stopColor="#8B6B2F"/>
      </linearGradient>
    </defs>
    {/* Closed leather book seen from the front, gold spine on the left */}
    <path
      d="M3.6 4.8 L11.5 6 L20.4 4.8 L20.4 19.2 L11.5 20.4 L3.6 19.2 Z"
      fill="url(#ledgerHdrG)"
      stroke="#7A5418"
      strokeWidth="0.5"
      strokeLinejoin="round"
    />
    {/* Binding crease down the middle */}
    <path d="M11.5 6 L11.5 20.4" stroke="#7A5418" strokeWidth="0.6"/>
    {/* Diamond-cut sigil in the center */}
    <path
      d="M11.5 11.2 L13.1 13.2 L11.5 15.2 L9.9 13.2 Z"
      fill="#FFF1B8"
      stroke="#7A5418"
      strokeWidth="0.3"
    />
  </svg>
);

/* ─────────────────── Character page (one entry per spread) ─────────────────── */

const CharacterPage = ({ entry, onEdit, onDelete, index, total }) => {
  return (
    <PaperPage>
      <div className="relative h-full flex flex-col px-6 pt-6 pb-12 overflow-y-auto">
        {/* Photograph or parchment silhouette */}
        <div className="relative mx-auto mb-3" style={{ width: 130, height: 170 }}>
          <div className="absolute inset-0 rounded-md overflow-hidden"
            style={{
              border: `2px solid ${GOLD}`,
              boxShadow: `0 4px 14px rgba(0,0,0,0.35), inset 0 0 0 1px ${GOLD_HI}77`,
              background: 'rgba(168,107,31,0.12)',
            }}>
            {entry.image_url ? (
              <img src={entry.image_url} alt={entry.name}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}/>
            ) : (
              /* Silhouette fallback when no image was uploaded */
              <svg viewBox="0 0 60 78" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
                <ellipse cx="30" cy="26" rx="13" ry="14" fill="rgba(82,46,8,0.55)"/>
                <path d="M8 78 Q8 50 30 50 Q52 50 52 78 Z" fill="rgba(82,46,8,0.55)"/>
              </svg>
            )}
          </div>
          {/* Corner brackets */}
          {[
            { top: -3,    left: -3,    rotate: 0 },
            { top: -3,    right: -3,   rotate: 90 },
            { bottom: -3, right: -3,   rotate: 180 },
            { bottom: -3, left: -3,    rotate: 270 },
          ].map((p, i) => (
            <svg key={i} width="12" height="12" viewBox="0 0 12 12"
              className="absolute" style={{
                top: p.top, left: p.left, right: p.right, bottom: p.bottom,
                transform: `rotate(${p.rotate}deg)`,
              }}>
              <path d="M1 0 V1 H0 V0 Z M0 4 V12 H1 V4 Z M4 0 H12 V1 H4 Z" fill={GOLD}/>
            </svg>
          ))}
        </div>

        {/* Name */}
        <p className="text-center font-['Cinzel'] uppercase tracking-[0.25em]"
          style={{
            color: INK, fontSize: 18,
            textShadow: '0 1px 0 rgba(245,217,122,0.5)',
            wordBreak: 'break-word',
          }}
          data-testid={`ledger-name-${entry.id}`}>
          {entry.name}
        </p>

        {/* Filigree under name */}
        <div className="mt-2 mx-auto flex items-center gap-2 w-full max-w-[180px]">
          <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}/>
          <svg width="8" height="8" viewBox="0 0 8 8">
            <path d="M4 0 L5.2 2.8 L8 4 L5.2 5.2 L4 8 L2.8 5.2 L0 4 L2.8 2.8 Z" fill={GOLD}/>
          </svg>
          <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}/>
        </div>

        {/* Description */}
        <p className="mt-4 font-['Cormorant_Garamond'] italic px-2"
          style={{
            color: 'rgba(26,18,8,0.85)',
            fontSize: 15,
            lineHeight: 1.55,
            textAlign: entry.description?.length > 80 ? 'justify' : 'center',
          }}
          data-testid={`ledger-description-${entry.id}`}>
          {entry.description || <span style={{ opacity: 0.45 }}>— no inscription yet —</span>}
        </p>

        {/* Edit / Delete actions */}
        <div className="mt-auto pt-4 flex items-center justify-center gap-3">
          <button onClick={() => onEdit(entry)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{
              background: 'rgba(168,107,31,0.15)',
              border: `1px solid ${GOLD}aa`,
              color: INK, fontFamily: "'Cinzel', serif", fontSize: 8.5,
              letterSpacing: '0.25em', textTransform: 'uppercase',
            }}
            data-testid={`ledger-edit-${entry.id}`}>
            <Pencil size={10}/> Edit
          </button>
          <button onClick={() => onDelete(entry)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style={{
              background: 'rgba(178,34,34,0.12)',
              border: '1px solid rgba(178,34,34,0.6)',
              color: '#7a1c1c', fontFamily: "'Cinzel', serif", fontSize: 8.5,
              letterSpacing: '0.25em', textTransform: 'uppercase',
            }}
            data-testid={`ledger-delete-${entry.id}`}>
            <Trash2 size={10}/> Erase
          </button>
        </div>

        {/* Page number */}
        <p className="absolute bottom-3 left-0 right-0 text-center font-['Cinzel']"
          style={{ color: 'rgba(26,18,8,0.45)', fontSize: 9, letterSpacing: '0.3em' }}>
          {index} of {total}
        </p>
      </div>
    </PaperPage>
  );
};

/* ─────────────────── Add / Edit form (in-page) ─────────────────── */

const EntryForm = ({ initial, onSubmit, onCancel, submitting, showCancel = true }) => {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(initial?.image_url || null);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), description: description.trim(), file });
  };

  return (
    <PaperPage>
      <form
        onSubmit={handleSubmit}
        className="relative h-full flex flex-col px-5 sm:px-6 pt-7 pb-5 min-h-0"
      >
        <p className="text-center font-['Cinzel'] uppercase tracking-[0.3em]"
          style={{ color: INK, fontSize: 11 }}>
          {initial ? 'Revise the page' : 'Inscribe a soul'}
        </p>
        <div className="mt-1 mx-auto h-px max-w-[60%]"
          style={{ background: `linear-gradient(90deg, transparent, ${GOLD}cc, transparent)` }}/>

        {/* Photo picker (optional) */}
        <div className="relative mx-auto mt-3 flex-shrink-0" style={{ width: 96, height: 122 }}>
          <button type="button" onClick={() => fileRef.current?.click()}
            className="absolute inset-0 rounded-md overflow-hidden flex items-center justify-center"
            style={{
              border: `2px dashed ${GOLD}`,
              background: 'rgba(168,107,31,0.12)',
              cursor: 'pointer',
            }}
            data-testid="ledger-form-image-picker">
            {preview ? (
              <img src={preview} alt="" className="w-full h-full object-cover"/>
            ) : (
              <div className="flex flex-col items-center gap-1" style={{ color: 'rgba(82,46,8,0.7)' }}>
                <ImageIcon size={22}/>
                <span className="font-['Outfit'] uppercase tracking-[0.2em]" style={{ fontSize: 8 }}>
                  Add photo
                </span>
                <span className="font-['Cormorant_Garamond'] italic" style={{ fontSize: 10 }}>
                  (optional)
                </span>
              </div>
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile}
            className="hidden" data-testid="ledger-form-file-input"/>
        </div>

        {/* Name (required) */}
        <label className="mt-3 font-['Cinzel'] uppercase tracking-[0.25em] flex-shrink-0"
          style={{ color: INK, fontSize: 9 }}>
          Name
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={80}
          className="mt-1 w-full bg-transparent px-2 py-1 outline-none flex-shrink-0"
          style={{
            borderBottom: `1px solid ${GOLD}aa`,
            fontFamily: "'Cinzel', serif",
            fontSize: 16,
            color: INK,
            letterSpacing: '0.05em',
            textAlign: 'center',
          }}
          placeholder="Their name"
          data-testid="ledger-form-name"
        />

        {/* Description (optional) — flex-1 fills remaining vertical space */}
        <label className="mt-3 font-['Cinzel'] uppercase tracking-[0.25em] flex-shrink-0"
          style={{ color: INK, fontSize: 9 }}>
          Inscription
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={1200}
          className="mt-1 w-full bg-transparent px-2 py-1 outline-none resize-none flex-1 min-h-[80px]"
          style={{
            border: `1px solid rgba(168,107,31,0.5)`,
            borderRadius: 4,
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: 'italic',
            fontSize: 14,
            color: 'rgba(26,18,8,0.85)',
            background: 'rgba(255,250,235,0.4)',
          }}
          placeholder="Their story, in your own words…"
          data-testid="ledger-form-description"
        />

        <div className="mt-3 flex items-center justify-center gap-2.5 flex-shrink-0">
          {showCancel && (
            <button type="button" onClick={onCancel}
              className="px-4 py-1.5 rounded-full"
              style={{
                background: 'rgba(168,107,31,0.12)',
                border: `1px solid ${GOLD}66`,
                color: INK, fontFamily: "'Cinzel', serif", fontSize: 9,
                letterSpacing: '0.25em', textTransform: 'uppercase',
              }}
              data-testid="ledger-form-cancel">
              Cancel
            </button>
          )}
          <button type="submit"
            disabled={submitting || !name.trim()}
            className="px-5 py-1.5 rounded-full flex items-center gap-1.5"
            style={{
              background: name.trim()
                ? `linear-gradient(140deg, ${GOLD_HI}, ${GOLD})`
                : 'rgba(168,107,31,0.2)',
              color: INK, fontFamily: "'Cinzel', serif", fontSize: 9,
              letterSpacing: '0.25em', textTransform: 'uppercase',
              boxShadow: name.trim() ? `0 0 12px ${GOLD}66` : 'none',
              opacity: submitting ? 0.6 : 1,
              cursor: name.trim() && !submitting ? 'pointer' : 'not-allowed',
            }}
            data-testid="ledger-form-submit">
            <Check size={11} strokeWidth={2.5}/>
            {submitting ? 'Inscribing…' : 'Inscribe'}
          </button>
        </div>
      </form>
    </PaperPage>
  );
};

/* ─────────────────── The Book (page-flip container) ─────────────────── */

export const ImperialLedger = ({ onClose }) => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(0);          // 0 cover, 1 inscribe-new, 2+ existing entries
  const [direction, setDirection] = useState(1); // +1 forward, -1 back (drives flip hinge)
  const [editTarget, setEditTarget] = useState(null);  // null = no edit overlay
  const [submitting, setSubmitting] = useState(false);

  // Load entries on mount
  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/ledger`);
      setEntries(Array.isArray(data) ? data : []);
    } catch (_) { /* silent */ }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // 0 = cover, 1 = inscribe-new, 2..N = existing entries
  const totalPages = 2 + entries.length;

  const turn = (delta) => {
    setDirection(delta > 0 ? 1 : -1);
    setPage((p) => Math.max(0, Math.min(totalPages - 1, p + delta)));
  };

  /* Touch swipe — REAL BOOK gesture:
       swipe LEFT→RIGHT (dx>0) → PREVIOUS page (back to cover)
       swipe RIGHT→LEFT (dx<0) → NEXT page */
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    const sx = touchStartX.current; const sy = touchStartY.current;
    touchStartX.current = null; touchStartY.current = null;
    if (sx == null) return;
    const dx = e.changedTouches[0].clientX - sx;
    const dy = e.changedTouches[0].clientY - (sy ?? 0);
    if (Math.abs(dx) < 60) return;
    if (Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) turn(1); else turn(-1);
  };

  const handleCreate = async ({ name, description, file }) => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('description', description);
      if (file) fd.append('file', file);
      const { data } = await axios.post(`${BACKEND_URL}/api/ledger`, fd);
      setEntries((prev) => [...prev, data]);
      setDirection(1);
      setPage(2 + entries.length);  // jump to the new soul's page
    } finally { setSubmitting(false); }
  };

  const handleUpdate = async ({ name, description }) => {
    if (!editTarget) return;
    setSubmitting(true);
    try {
      const { data } = await axios.patch(
        `${BACKEND_URL}/api/ledger/${editTarget.id}`,
        { name, description }
      );
      setEntries((prev) => prev.map((e) => e.id === data.id ? { ...e, ...data } : e));
      setEditTarget(null);
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Erase ${entry.name} from the ledger? This cannot be undone.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/ledger/${entry.id}`);
      const newEntries = entries.filter((e) => e.id !== entry.id);
      setEntries(newEntries);
      setDirection(-1);
      // Land on the previous entry or fall back to the inscribe page
      setPage(Math.max(1, page - 1));
    } catch (_) { /* silent */ }
  };

  // Determine what to render based on the page index + edit overlay
  let pageContent;
  if (editTarget) {
    pageContent = (
      <EntryForm
        initial={editTarget}
        onSubmit={handleUpdate}
        onCancel={() => setEditTarget(null)}
        submitting={submitting}
        showCancel={true}
      />
    );
  } else if (page === 0) {
    pageContent = <CoverPage profilePicture={user?.profile_picture} nickname={user?.nickname} />;
  } else if (page === 1) {
    // Always the inscribe-new page (no roster)
    pageContent = (
      <EntryForm
        onSubmit={handleCreate}
        submitting={submitting}
        showCancel={false}
      />
    );
  } else {
    const entry = entries[page - 2];
    if (!entry) {
      pageContent = <CoverPage profilePicture={user?.profile_picture} nickname={user?.nickname} />;
    } else {
      pageContent = (
        <CharacterPage
          entry={entry}
          index={page - 1}
          total={entries.length + 1}
          onEdit={(e) => setEditTarget(e)}
          onDelete={handleDelete}
        />
      );
    }
  }

  const canTurn = !editTarget;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[300] flex flex-col imperium-shell"
      style={{
        background:
          'radial-gradient(ellipse at top, #1a1208 0%, #060403 60%, #000 100%)',
      }}
      data-testid="imperial-ledger">

      {/* Top bar — mirrors Royal Quote Book */}
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{
          paddingTop: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 12px)',
          borderBottom: '1px solid rgba(212,175,55,0.18)',
        }}
      >
        <BackPill onClick={onClose} accent="#C98A3A" testId="ledger-close"/>
        <div className="flex flex-col items-center">
          <h2
            className="font-['Cormorant_Garamond'] text-xl font-light tracking-wide flex items-center gap-2"
            style={{ color: GOLD_HI }}
          >
            <LedgerHeaderGlyph size={18}/>
            The Ledger of Souls
          </h2>
          <p className="font-['Outfit'] text-[10px] tracking-[0.18em] text-white/30 mt-0.5">
            {editTarget ? 'REVISE'
              : page === 0 ? 'COVER'
              : page === 1 ? 'NEW SOUL'
              : `PAGE ${page - 1} / ${Math.max(1, entries.length)}`}
          </p>
        </div>
        <div className="w-6" />
      </div>

      {/* Book stage — expanded; swipe RIGHT → next, swipe LEFT → prev (→ cover). */}
      <div className="flex-1 flex items-center justify-center px-2 pb-3 min-h-0"
        style={{ paddingBottom: 'calc(12px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))' }}
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div
          className="relative w-full max-w-[480px] h-full"
          style={{ perspective: '1800px' }}
        >
          <AnimatePresence custom={direction} mode="wait">
            <motion.div
              key={`${page}-${editTarget?.id || 'view'}`}
              custom={direction}
              initial={{ opacity: 0, rotateY: direction > 0 ? 170 : -170 }}
              animate={{ opacity: 1, rotateY: 0 }}
              exit={
                direction > 0
                  ? { rotateY: -170, opacity: 0 }
                  : { rotateY: 170,  opacity: 0 }
              }
              transition={{
                rotateY: { duration: 0.7, ease: [0.55, 0.05, 0.25, 0.98] },
                opacity: { duration: 0.55 },
              }}
              style={{
                position: 'absolute', inset: 0,
                transformOrigin: direction > 0 ? 'left center' : 'right center',
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden',
                boxShadow: '0 8px 28px rgba(0,0,0,0.6), inset 0 0 40px rgba(0,0,0,0.25)',
              }}
            >
              {pageContent}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

/* ─────────────────── Tile icon (book glyph for the launcher) ─────────────────── */

export const LedgerTileIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <defs>
      <linearGradient id="ledgerG" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#F5D97A"/>
        <stop offset="100%" stopColor="#8B6B2F"/>
      </linearGradient>
    </defs>
    <path d="M3 4.5 L11.5 6 L20.5 4.5 L20.5 19 L11.5 20.5 L3 19 Z"
      fill="url(#ledgerG)" stroke="#D4AF37" strokeWidth="0.6" strokeLinejoin="round"/>
    <path d="M11.5 6 L11.5 20.5" stroke="#8B6B2F" strokeWidth="0.7"/>
    {/* Center sigil */}
    <circle cx="11.5" cy="13" r="1.6" fill="#FFE9A0" stroke="#8B6B2F" strokeWidth="0.4"/>
  </svg>
);

export default ImperialLedger;
